import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { IS_STRIPPED_DOWN } from './mode';

// Supabase client for the Dummy Pot ledger.
//
// Looks in two places so the same build works across bundler conventions:
//   REACT_APP_SUPABASE_URL       — CRA-style (what this project uses today)
//   VITE_SUPABASE_URL            — Vite-style (in case we migrate / mirror)
// ...and likewise for the anon key. Whichever is populated wins; we log
// the source to the console so Dromide can confirm from DevTools which
// env var the running bundle actually resolved.

const URL =
  process.env.REACT_APP_SUPABASE_URL ||
  process.env.VITE_SUPABASE_URL;

const KEY =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.VITE_SUPABASE_ANON_KEY;

const urlSource = process.env.REACT_APP_SUPABASE_URL
  ? 'REACT_APP_SUPABASE_URL'
  : process.env.VITE_SUPABASE_URL
    ? 'VITE_SUPABASE_URL'
    : '(none)';

const keySource = process.env.REACT_APP_SUPABASE_ANON_KEY
  ? 'REACT_APP_SUPABASE_ANON_KEY'
  : process.env.VITE_SUPABASE_ANON_KEY
    ? 'VITE_SUPABASE_ANON_KEY'
    : '(none)';

if (typeof window !== 'undefined') {
  console.log(
    `[Supabase] URL source: ${urlSource} · KEY source: ${keySource} · connected: ${!!(URL && KEY)}`
  );
}

// ── Client initialization (Session B recovery 2026-05-13) ──────────────
//
// Earlier in the ui-sandbox branch's life, Supabase was deliberately
// disabled here — the prior comment read *"so no tester action can
// reach the live 'Truth' ledger — no INSERTs to public.pledges, no
// UPDATEs to public.profiles, no realtime channels"*. That shutoff
// was correct when ui-sandbox was a pure-localStorage demo branch
// with no Supabase target.
//
// Per DECISIONS.md 2026-05-11 (Postgres engine is Live target;
// Supabase is sandbox/demo only) and the Session A parimutuel port
// at migration 20260513000000, **the Arena Supabase project
// (revhjqwhglqxmogpkcui) is now the sandbox-grade live ledger
// target for ui-sandbox**. The shutoff has been obsolete since
// Session A landed; it was never reverted because:
//   - Session A only touched the database layer.
//   - Session B's recordPledgeRemote rewrite implicitly assumed
//     supabase was non-null.
//   - The drift tests (test_supabase_pricing.py +
//     test_pricing_typescript.mjs) bypass the React-app path
//     entirely — they use SUPABASE_SERVICE_ROLE_KEY directly.
//   - No session's brief named supabaseClient.ts as a target,
//     so no audit pass inspected it.
//
// Gate-22 SQL diagnostic against Arena (`SELECT … FROM pledges
// GROUP BY market_id, outcome_name` returning 0 rows after a
// successful-looking pledge) caught the gap on 2026-05-13.
// Vercel env-var verification surfaced a related issue: preview
// env vars were empty strings; both have now been pointed at
// Arena. See findings file §5.6 for the full multi-session
// oversight account.
//
// ── Blocklist guard (defence in depth) ──────────────────────────
//
// The Live Supabase project ref `zdtfjlkkjosjrjzffosd` (per
// CRYPTO_ARCHITECTURE.md / DECISIONS.md 2026-05-11) is the
// production target — real-money path, IoM-grade compliance.
// **If ui-sandbox's resolved URL ever points there, we refuse to
// initialize the client** and force the localStorage fallback
// path in lib/ledger.ts, with a loud console error. Arena
// (`revhjqwhglqxmogpkcui`) is the only legitimate URL for
// ui-sandbox pledges. Belt-and-brace on top of Vercel's env-var
// config: misconfiguration produces "no real pledges" rather
// than "pledges contaminating Live production". When Live phase
// arrives and we have a separate code path for Live writes, this
// blocklist may need lifting in that context; for now,
// ui-sandbox is Arena-only.

const LIVE_PROJECT_REF = 'zdtfjlkkjosjrjzffosd';

function isBlockedUrl(url: string | undefined): boolean {
  return !!url && url.includes(LIVE_PROJECT_REF);
}

const blocked = isBlockedUrl(URL);
if (blocked && typeof window !== 'undefined') {
  console.error(
    `[Supabase] BLOCKED: resolved URL "${URL}" points at the Live ` +
    `project ref "${LIVE_PROJECT_REF}" — ui-sandbox writes must ` +
    `target the Arena project (revhjqwhglqxmogpkcui). Client ` +
    `forced to null; the cockpit will fall back to localStorage. ` +
    `Fix the Vercel env vars or local .env file to point at Arena.`,
  );
}

// Auth options (2026-05-15, Arena enrolment debugging):
//
//   flowType: 'implicit'
//     The Arena enrolment surface uses magic-links. Supabase v2's
//     default is PKCE, which puts a `?code=…` query param on the
//     redirect and requires a code-verifier in client storage at the
//     moment the click-through lands. If anything cleared the
//     verifier between the ENROL click and the magic-link landing
//     (different tab, expired sessionStorage, mobile-handoff,
//     intermediate redirect that hit a fresh storage scope), the
//     exchange silently fails — URL params get scrubbed by the
//     client's detect-and-clean step but no `sb-…-auth-token` ever
//     lands in localStorage. Implicit flow puts the access_token in
//     the URL fragment; the client reads it on detectSessionInUrl
//     without needing prior client state.
//
//   detectSessionInUrl: true
//     Default in v2; restated explicitly so the contract is visible
//     in this file and not at the mercy of upstream defaults.
//
//   persistSession + autoRefreshToken
//     Defaults restated for the same reason.
//
//   debug
//     Surfaces the auth state machine in DevTools — invaluable next
//     time the magic-link round-trip misbehaves. Off in production
//     to keep the console quiet.
export const supabase: SupabaseClient | null =
  URL && KEY && !blocked
    ? createClient(URL, KEY, {
        auth: {
          flowType:           'implicit',
          detectSessionInUrl: true,
          persistSession:     true,
          autoRefreshToken:   true,
          debug:              process.env.NODE_ENV !== 'production',
        },
      })
    : null;

export function isSupabaseEnabled(): boolean {
  return !!(URL && KEY && !blocked);
}

// Exposed for the diagnostic-dot indicator in the header.
export const SUPABASE_DIAG = {
  urlSource,
  keySource,
  connected: !!(URL && KEY && !blocked),
  blocked,
};

if (typeof window !== 'undefined') {
  if (supabase) {
    console.log(
      `[Supabase] client initialized · URL: ${URL} · ` +
      `keySource: ${keySource} · target: Arena (sandbox-grade live ledger)`,
    );
  } else if (!URL || !KEY) {
    console.log('[Supabase] env vars missing — client null, falling back to localStorage');
  }
  // If blocked, the console.error above already fired.
}

// ─── Ghost Citizen ID ────────────────────────────────────────────────────
// Anonymous per-browser identity for the Dummy Pot. Bypasses formal auth:
// on first load we mint a clean string id in the form `citizen_XXXXXXXXXXXX`
// (12 random digits) and persist it in localStorage. Using a plain
// citizen_ prefix rather than a UUID keeps the id both human-readable
// and friendly to TEXT-typed columns on the Supabase side — no casts,
// no hyphens, no accidental uuid validation traps.

const GHOST_KEY = 'agoracle_ghost_id';

function mintCitizenId(): string {
  // 12 random digits → 'citizen_482910375264'
  let suffix = '';
  for (let i = 0; i < 12; i++) suffix += Math.floor(Math.random() * 10).toString();
  return `citizen_${suffix}`;
}

export function getGhostId(): string {
  if (typeof window === 'undefined') return 'citizen_ssr';
  try {
    const existing = window.localStorage.getItem(GHOST_KEY);
    // Migrate any pre-existing UUID-style value to the new citizen_ format.
    if (existing && existing.startsWith('citizen_')) return existing;
    const id = mintCitizenId();
    window.localStorage.setItem(GHOST_KEY, id);
    return id;
  } catch {
    return 'citizen_anon';
  }
}

// Short display form for the header pill — e.g. 'USER · 482910' on
// Arena (IS_STRIPPED_DOWN) or 'CITIZEN · 482910' on Live.
//
// Session B Phase C revision-3 (2026-05-13): the hardcoded
// "CITIZEN · " prefix was ceremonial-vocabulary leakage that
// predates the inventory's surface classification — even with
// IS_STRIPPED_DOWN correctly applied across every other surface,
// the pill still announced "CITIZEN" regardless of tier. Per
// I-046's plain-operational-language rule for the Arena cockpit
// (DECISIONS.md 2026-05-13), the stripped path uses "USER" —
// most operational, least ceremonial.
export function formatGhostId(id: string): string {
  const parts  = id.split('_');
  const suffix = (parts[parts.length - 1] || id).slice(0, 6).toUpperCase();
  const prefix = IS_STRIPPED_DOWN ? 'USER' : 'CITIZEN';
  return `${prefix} · ${suffix}`;
}

// Leaderboard-friendly form — e.g. 'User-4829' (stripped) /
// 'Citizen-4829' (Live) / '0x1a2b…f09c' (wallet). Shorter than the
// header pill so four entries stack cleanly on narrow columns.
// Handles both Ghost users and Agoracle Global wallet addresses
// so the same leaderboard can mix identity types.
//
// Note: on the stripped-down Arena, Leaderboard is held back per
// I-046 (mount-gated in MultiPage.tsx), so this function's
// stripped branch is rarely exercised. Belt-and-brace though —
// the function is exported and could be reused elsewhere; the
// vocabulary gate stays consistent.
export function shortCitizen(id: string): string {
  if (!id) return 'Anon';
  if (/^0x[0-9a-fA-F]{6,}$/.test(id)) {
    return `${id.slice(0, 6)}…${id.slice(-4)}`;
  }
  if (id.startsWith('citizen_')) {
    const parts  = id.split('_');
    const suffix = (parts[parts.length - 1] || id).slice(0, 4);
    const prefix = IS_STRIPPED_DOWN ? 'User' : 'Citizen';
    return `${prefix}-${suffix}`;
  }
  return id;
}

// ─── Dev helpers ─────────────────────────────────────────────────────────
// Exposed on window.agoracle so Dromide can reset a test browser to a
// clean slate from DevTools. Documented in docs/agoracle-developer-brief.md.

const LEDGER_STORAGE_KEY = 'agoracle_ledger_v1';

if (typeof window !== 'undefined') {
  (window as unknown as { agoracle?: Record<string, unknown> }).agoracle = {
    whoami(): string | null {
      try { return window.localStorage.getItem(GHOST_KEY); } catch { return null; }
    },
    // SANDBOX BRANCH: Supabase is always null here, so the remote DELETE
    // path from main is dead code. Local-only nuke clears the ghost id
    // and the ledger cache and reloads — a fresh citizen_ is minted on
    // the next render.
    async nuke(): Promise<void> {
      try {
        window.localStorage.removeItem(GHOST_KEY);
        window.localStorage.removeItem(LEDGER_STORAGE_KEY);
      } catch (e) { console.warn('[nuke:local]', e); }
      window.location.reload();
    },
  };
}
